import { createContext, useContext } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import type { DeveloperToolsConfig } from "../developer-tools";

/**
 * Shell-level runtime configuration that is fixed for the lifetime of the
 * mounted shell — i.e. set once at app startup and constant across views,
 * routes, navigations, and revalidations.
 *
 * Distinct from per-view state (see {@link ViewIdentity}) and from per-call
 * data (see {@link RouteState}). Anything that genuinely changes per view
 * does NOT belong here.
 *
 * The shape is intentionally an object (not a single value) so future
 * shell-static fields — e.g. a telemetry endpoint, an auth-header builder —
 * can land without breaking the consumer contract or proliferating
 * sibling contexts.
 */
export type ShellRuntimeConfig = {
  /** Base URL for POSTs to the Tinkertoy server `/action` endpoint. */
  apiBaseUrl: string;
  /** Non-production developer tools metadata; omitted in production. */
  developerTools?: DeveloperToolsConfig;
};

const ShellRuntimeContext = createContext<ShellRuntimeConfig | null>(null);

export interface ShellRuntimeProviderProps {
  value: ShellRuntimeConfig;
  children: ReactNode;
}

/**
 * Provides {@link ShellRuntimeConfig} to the entire shell subtree.
 *
 * Inside this package it is rendered once by `InternalTinkertoyProvider`,
 * which is what the shell's own `ShellRoot` uses. The component is also
 * re-exported from the package root so that host apps and tests can stand
 * up an isolated provider tree (e.g. mounting a single module/view in
 * Storybook or a unit test) without pulling in the full shell.
 *
 * Callers should pass a stable `value` reference (memoize on the
 * primitive fields) so consumers' `useMemo` dependencies don't fire on
 * every parent render.
 */
export function ShellRuntimeProvider({
  value,
  children,
}: ShellRuntimeProviderProps) {
  return (
    <ShellRuntimeContext.Provider value={value}>
      {children}
    </ShellRuntimeContext.Provider>
  );
}

/**
 * Read the current shell's {@link ShellRuntimeConfig} from context.
 *
 * @throws {Error} when called outside a {@link ShellRuntimeProvider},
 * which usually means the component is rendered outside the Tinkertoy
 * shell's provider tree (i.e. outside the tree set up by `ShellRoot`
 * or wrapped in `InternalTinkertoyProvider` in tests).
 */
export function useShellRuntime(): ShellRuntimeConfig {
  const ctx = useContext(ShellRuntimeContext);
  if (ctx === null) {
    throw new Error(
      "useShellRuntime() must be called inside the Tinkertoy shell. " +
        "Ensure the component is rendered within the tree created by ShellRoot " +
        "(or wrapped in InternalTinkertoyProvider in tests).",
    );
  }
  return ctx;
}
